import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import AcademicsFormField from '../../components/AcademicsFormField';
import Img from '../../../../Img';
import PopupV2 from '../../../../Popup/PopupV2';
import SchoolSearch from '../../../../Form/SchoolSearch';
import { getPlayerStudentInfo, updatePlayerStudentInfo } from '../../../../../actions/profile-actions';
import useCPPTracking from '../../../../hooks/useCPPTracking';
import { CppPlayerActions } from '../../../../Analytics/constants';
import { SUPPORTED_FILE_ACCEPT, SUPPORTED_FILE_EXTENSIONS, SUPPORTED_FILE_DISPLAY_LABEL, appendIfPresent, validateFile } from '../academicsFileUpload';
import styles from '../../collegeProspectTab.scss';

const TESTID_PREFIX = 'modal.profile.college-prospect.academics-edit';
const GRAD_YEAR_RANGE = 7;

const GRAD_MONTH_OPTIONS = [
    { value: 0, label: 'January' },
    { value: 1, label: 'February' },
    { value: 2, label: 'March' },
    { value: 3, label: 'April' },
    { value: 4, label: 'May' },
    { value: 5, label: 'June' },
    { value: 6, label: 'July' },
    { value: 7, label: 'August' },
    { value: 8, label: 'September' },
    { value: 9, label: 'October' },
    { value: 10, label: 'November' },
    { value: 11, label: 'December' },
];

const buildGradYearOptions = () => {
    const currentYear = moment.utc().year();
    return Array.from({ length: GRAD_YEAR_RANGE }, (_, i) => currentYear + i);
};

const AcademicsEditModal = ({ onClose }) => {
    const dispatch = useDispatch();
    const { trackPlayerEngagement } = useCPPTracking();
    const studentInfo = useSelector((state) => state.profile?.data?.studentInfo) || {};

    const initialGradYear = studentInfo.graduationDate
        ? moment.utc(studentInfo.graduationDate).year()
        : '';
    const initialGradMonth = studentInfo.graduationDate
        ? moment.utc(studentInfo.graduationDate).month()
        : '';

    const [highSchoolName, setHighSchoolName] = useState(studentInfo.highSchoolName || '');
    const [highSchoolLocation, setHighSchoolLocation] = useState(studentInfo.highSchoolLocation || '');
    const [graduationYear, setGraduationYear] = useState(initialGradYear);
    const [graduationMonth, setGraduationMonth] = useState(initialGradMonth);
    const [highSchoolGpa, setHighSchoolGpa] = useState(studentInfo.highSchoolGpa ?? '');
    const [sat, setSat] = useState(studentInfo.scores?.sat ?? '');
    const [act, setAct] = useState(studentInfo.scores?.act ?? '');
    const [toefl, setToefl] = useState(studentInfo.scores?.toefl ?? '');
    const [coursework, setCoursework] = useState(studentInfo.coursework || '');
    const [majorOfInterest, setMajorOfInterest] = useState(studentInfo.majorOfInterest || '');
    const [transcriptFile, setTranscriptFile] = useState(null);
    const [tennisResumeFile, setTennisResumeFile] = useState(null);
    const [removeTranscript, setRemoveTranscript] = useState(false);
    const [removeTennisResume, setRemoveTennisResume] = useState(false);
    const [transcriptError, setTranscriptError] = useState(null);
    const [tennisResumeError, setTennisResumeError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleTranscriptChange = (event) => {
        const file = event.target.files?.[0] || null;
        const validationError = validateFile(file, 'Transcript');
        if (validationError) {
            setTranscriptError(validationError);
            setTranscriptFile(null);
            event.target.value = '';
            return;
        }
        setTranscriptError(null);
        setTranscriptFile(file);
        setRemoveTranscript(false);
    };

    const handleTennisResumeChange = (event) => {
        const file = event.target.files?.[0] || null;
        const validationError = validateFile(file, 'Tennis resume');
        if (validationError) {
            setTennisResumeError(validationError);
            setTennisResumeFile(null);
            event.target.value = '';
            return;
        }
        setTennisResumeError(null);
        setTennisResumeFile(file);
        setRemoveTennisResume(false);
    };

    const playerId = useSelector((state) => state.profile?.data?.id);

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (transcriptError || tennisResumeError) {
            setError('Please pick a supported file and try again.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            // The API's switch on `Context` scopes which fields get persisted:
            // `onboardingSchoolInfo` handles HighSchoolName/Location/GraduationDate,
            // `onboardingAcademics` handles GPA/Coursework/Major/Scores/files. The
            // single Academics card collects both buckets, so we fire two scoped
            // updates back-to-back rather than risk the catch-all `default` branch
            // overwriting coach fields with null.
            const schoolFormData = new FormData();
            schoolFormData.append('Context', 'onboardingSchoolInfo');
            appendIfPresent(schoolFormData, 'highSchoolName', highSchoolName);
            appendIfPresent(schoolFormData, 'highSchoolLocation', highSchoolLocation);
            if (graduationYear) {
                const monthNumber = graduationMonth === '' ? 0 : Number(graduationMonth);
                const gradDate = moment.utc({ year: Number(graduationYear), month: monthNumber, day: 1 }).format();
                schoolFormData.append('graduationDate', gradDate);
            }

            const academicsFormData = new FormData();
            academicsFormData.append('Context', 'onboardingAcademics');
            appendIfPresent(academicsFormData, 'highSchoolGpa', highSchoolGpa);
            appendIfPresent(academicsFormData, 'coursework', coursework);
            appendIfPresent(academicsFormData, 'majorOfInterest', majorOfInterest);
            appendIfPresent(academicsFormData, 'scores.sat', sat);
            appendIfPresent(academicsFormData, 'scores.act', act);
            appendIfPresent(academicsFormData, 'scores.toefl', toefl);
            const updateTranscript = !!transcriptFile || removeTranscript;
            const updateTennisResume = !!tennisResumeFile || removeTennisResume;
            academicsFormData.append('updateTranscript', String(updateTranscript));
            academicsFormData.append('updateTennisResume', String(updateTennisResume));
            if (transcriptFile) {
                academicsFormData.append('transcript', transcriptFile);
            }
            if (tennisResumeFile) {
                academicsFormData.append('tennisResume', tennisResumeFile);
            }

            const schoolOk = await Promise.resolve(dispatch(updatePlayerStudentInfo(schoolFormData)));
            if (!schoolOk) {
                throw new Error('school save failed');
            }
            const academicsOk = await Promise.resolve(dispatch(updatePlayerStudentInfo(academicsFormData)));
            if (!academicsOk) {
                throw new Error('academics save failed');
            }
            if (playerId) {
                await Promise.resolve(dispatch(getPlayerStudentInfo(playerId)));
            }
            trackPlayerEngagement(CppPlayerActions.CPP_ACADEMICS_UPDATED);
            onClose();
        } catch (err) {
            const stage = err?.message?.includes('school') ? 'school info' : 'academics';
            const serverMessage = err?.response?.data?.DisplayMessage;
            const detail = serverMessage ||
                `Make sure your transcript and tennis resume files are one of: ${SUPPORTED_FILE_EXTENSIONS.map(e => e.toUpperCase()).join(', ')}.`;
            setError(`We couldn't save your ${stage}. ${detail}`);
        } finally {
            setSubmitting(false);
        }
    };

    const gradYearOptions = buildGradYearOptions();

    const transcriptDisplayName = transcriptFile?.name ||
        (studentInfo.transcriptFileName && !removeTranscript ? studentInfo.transcriptFileName : null);
    const tennisResumeDisplayName = tennisResumeFile?.name ||
        (studentInfo.tennisResumeFileName && !removeTennisResume ? studentInfo.tennisResumeFileName : null);

    return (
        <PopupV2
            onCloseClick={onClose}
            config={{ size: 'medium', customHeader: () => null }}
        >
            <form
                onSubmit={handleSubmit}
                className={styles.academicsEditForm}
                data-testid={`${TESTID_PREFIX}.container`}
            >
                <div className={styles.academicsEditTitle} data-testid={`${TESTID_PREFIX}.title`}>
                    Edit Academic Info
                </div>

                <div className={styles.academicsEditSection} data-testid={`${TESTID_PREFIX}.school-section`}>
                    <div className={styles.academicsEditSectionTitle} data-testid={`${TESTID_PREFIX}.school-section.title`}>
                        School
                    </div>
                    <AcademicsFormField label="High School" testId="high-school" testIdPrefix={TESTID_PREFIX}>
                        <SchoolSearch
                            data-testid={`${TESTID_PREFIX}.high-school`}
                            className="form-control"
                            searchContainerClassName="mt0 mb0"
                            placeholder="Search school by name"
                            clearAfterSelect={false}
                            closeAfterSelect={true}
                            searchEndPoint="/highSchools"
                            schoolClubSearch={false}
                            displaySchoolNameAfterSelect={true}
                            showSearchClearIcon={true}
                            query={highSchoolName}
                            onChange={(query) => setHighSchoolName(query)}
                            handleSchoolSelect={(school) => {
                                setHighSchoolName(school.name || '');
                                const locationString = school.location?.googleFormattedName || school.location?.display || '';
                                if (locationString) {
                                    setHighSchoolLocation(locationString);
                                }
                            }}
                            handleClearSchoolClick={() => {
                                setHighSchoolName('');
                                setHighSchoolLocation('');
                            }}
                        />
                    </AcademicsFormField>
                    <div className={styles.academicsEditRow} data-testid={`${TESTID_PREFIX}.school-row`}>
                        <AcademicsFormField label="Location" testId="high-school-location" testIdPrefix={TESTID_PREFIX}>
                            <input
                                type="text"
                                value={highSchoolLocation}
                                onChange={(e) => setHighSchoolLocation(e.target.value)}
                                placeholder="City, State"
                                data-testid={`${TESTID_PREFIX}.high-school-location`}
                            />
                        </AcademicsFormField>
                        <AcademicsFormField label="Grad Month" testId="graduating-month" testIdPrefix={TESTID_PREFIX}>
                            <select
                                value={graduationMonth}
                                onChange={(e) => setGraduationMonth(e.target.value)}
                                data-testid={`${TESTID_PREFIX}.graduating-month`}
                            >
                                <option value="">Select month</option>
                                {GRAD_MONTH_OPTIONS.map((month) => (
                                    <option key={month.value} value={month.value}>{month.label}</option>
                                ))}
                            </select>
                        </AcademicsFormField>
                        <AcademicsFormField label="Grad Year" testId="graduating-year" testIdPrefix={TESTID_PREFIX}>
                            <select
                                value={graduationYear}
                                onChange={(e) => setGraduationYear(e.target.value)}
                                data-testid={`${TESTID_PREFIX}.graduating-year`}
                            >
                                <option value="">Select year</option>
                                {gradYearOptions.map((year) => (
                                    <option key={year} value={year}>{year}</option>
                                ))}
                            </select>
                        </AcademicsFormField>
                    </div>
                </div>

                <div className={styles.academicsEditSection} data-testid={`${TESTID_PREFIX}.scores-section`}>
                    <div className={styles.academicsEditSectionTitle} data-testid={`${TESTID_PREFIX}.scores-section.title`}>
                        Scores
                    </div>
                    <div className={styles.academicsEditRow} data-testid={`${TESTID_PREFIX}.scores-row-1`}>
                        <AcademicsFormField label="GPA" testId="high-school-gpa" testIdPrefix={TESTID_PREFIX}>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                max="5"
                                value={highSchoolGpa}
                                onChange={(e) => setHighSchoolGpa(e.target.value)}
                                placeholder="e.g. 3.8"
                                data-testid={`${TESTID_PREFIX}.high-school-gpa`}
                            />
                        </AcademicsFormField>
                        <AcademicsFormField label="SAT" testId="sat" testIdPrefix={TESTID_PREFIX}>
                            <input
                                type="number"
                                min="0"
                                max="1600"
                                value={sat}
                                onChange={(e) => setSat(e.target.value)}
                                placeholder="400 - 1600"
                                data-testid={`${TESTID_PREFIX}.sat`}
                            />
                        </AcademicsFormField>
                    </div>
                    <div className={styles.academicsEditRow} data-testid={`${TESTID_PREFIX}.scores-row-2`}>
                        <AcademicsFormField label="ACT" testId="act" testIdPrefix={TESTID_PREFIX}>
                            <input
                                type="number"
                                min="1"
                                max="36"
                                value={act}
                                onChange={(e) => setAct(e.target.value)}
                                placeholder="1 - 36"
                                data-testid={`${TESTID_PREFIX}.act`}
                            />
                        </AcademicsFormField>
                        <AcademicsFormField label="TOEFL" testId="toefl" testIdPrefix={TESTID_PREFIX}>
                            <input
                                type="number"
                                min="0"
                                max="120"
                                value={toefl}
                                onChange={(e) => setToefl(e.target.value)}
                                placeholder="0 - 120"
                                data-testid={`${TESTID_PREFIX}.toefl`}
                            />
                        </AcademicsFormField>
                    </div>
                </div>

                <div className={styles.academicsEditSection} data-testid={`${TESTID_PREFIX}.interests-section`}>
                    <div className={styles.academicsEditSectionTitle} data-testid={`${TESTID_PREFIX}.interests-section.title`}>
                        Interests
                    </div>
                    <AcademicsFormField label="Current Coursework" testId="coursework" testIdPrefix={TESTID_PREFIX}>
                        <textarea
                            rows="3"
                            maxLength="500"
                            value={coursework}
                            onChange={(e) => setCoursework(e.target.value)}
                            placeholder="e.g. AP English"
                            data-testid={`${TESTID_PREFIX}.coursework`}
                        />
                    </AcademicsFormField>
                    <AcademicsFormField label="Major of Interest" testId="major-of-interest" testIdPrefix={TESTID_PREFIX}>
                        <input
                            type="text"
                            maxLength="100"
                            value={majorOfInterest}
                            onChange={(e) => setMajorOfInterest(e.target.value)}
                            placeholder="e.g. Business"
                            data-testid={`${TESTID_PREFIX}.major-of-interest`}
                        />
                    </AcademicsFormField>
                </div>

                <div className={styles.academicsEditSection} data-testid={`${TESTID_PREFIX}.documents-section`}>
                    <div className={styles.academicsEditSectionTitle} data-testid={`${TESTID_PREFIX}.documents-section.title`}>
                        Documents
                    </div>
                    <div className={styles.academicsEditRow} data-testid={`${TESTID_PREFIX}.documents-row`}>
                        <AcademicsFormField label="Transcript" testId="transcript" testIdPrefix={TESTID_PREFIX}>
                            <div className={styles.academicsEditFileBox} data-testid={`${TESTID_PREFIX}.transcript-file-wrapper`}>
                                <span className={styles.academicsEditFileHint} data-testid={`${TESTID_PREFIX}.transcript-hint`}>
                                    {SUPPORTED_FILE_DISPLAY_LABEL}
                                </span>
                                <label
                                    className={styles.academicsEditUploadLink}
                                    data-testid={`${TESTID_PREFIX}.transcript-upload-label`}
                                >
                                    <span data-testid={`${TESTID_PREFIX}.transcript-upload-text`}>
                                        {transcriptDisplayName ? 'Change' : 'Upload'}
                                    </span>
                                    <input
                                        type="file"
                                        accept={SUPPORTED_FILE_ACCEPT}
                                        onChange={handleTranscriptChange}
                                        className={styles.academicsEditFileInput}
                                        data-testid={`${TESTID_PREFIX}.transcript-file`}
                                    />
                                </label>
                            </div>
                            {transcriptDisplayName && (
                                <span
                                    className={styles.academicsEditFileSelected}
                                    data-testid={`${TESTID_PREFIX}.transcript-selected`}
                                >
                                    <Img
                                        src="/img/v4/circle-check-green.svg"
                                        alt=""
                                        className={styles.academicsEditFileSelectedIcon}
                                    />
                                    {transcriptDisplayName}
                                </span>
                            )}
                            {transcriptError && (
                                <small
                                    className={styles.academicsEditError}
                                    data-testid={`${TESTID_PREFIX}.transcript-error`}
                                >
                                    {transcriptError}
                                </small>
                            )}
                            {studentInfo.transcriptFileName && !removeTranscript && (
                                <button
                                    type="button"
                                    className={styles.academicsEditRemoveBtn}
                                    onClick={() => {
                                        setRemoveTranscript(true);
                                        setTranscriptFile(null);
                                        setTranscriptError(null);
                                    }}
                                    data-testid={`${TESTID_PREFIX}.transcript-remove`}
                                >
                                    Remove current transcript
                                </button>
                            )}
                        </AcademicsFormField>
                        <AcademicsFormField label="Tennis Resume" testId="tennis-resume" testIdPrefix={TESTID_PREFIX}>
                            <div className={styles.academicsEditFileBox} data-testid={`${TESTID_PREFIX}.tennis-resume-file-wrapper`}>
                                <span className={styles.academicsEditFileHint} data-testid={`${TESTID_PREFIX}.tennis-resume-hint`}>
                                    {SUPPORTED_FILE_DISPLAY_LABEL}
                                </span>
                                <label
                                    className={styles.academicsEditUploadLink}
                                    data-testid={`${TESTID_PREFIX}.tennis-resume-upload-label`}
                                >
                                    <span data-testid={`${TESTID_PREFIX}.tennis-resume-upload-text`}>
                                        {tennisResumeDisplayName ? 'Change' : 'Upload'}
                                    </span>
                                    <input
                                        type="file"
                                        accept={SUPPORTED_FILE_ACCEPT}
                                        onChange={handleTennisResumeChange}
                                        className={styles.academicsEditFileInput}
                                        data-testid={`${TESTID_PREFIX}.tennis-resume-file`}
                                    />
                                </label>
                            </div>
                            {tennisResumeDisplayName && (
                                <span
                                    className={styles.academicsEditFileSelected}
                                    data-testid={`${TESTID_PREFIX}.tennis-resume-selected`}
                                >
                                    <Img
                                        src="/img/v4/circle-check-green.svg"
                                        alt=""
                                        className={styles.academicsEditFileSelectedIcon}
                                    />
                                    {tennisResumeDisplayName}
                                </span>
                            )}
                            {tennisResumeError && (
                                <small
                                    className={styles.academicsEditError}
                                    data-testid={`${TESTID_PREFIX}.tennis-resume-error`}
                                >
                                    {tennisResumeError}
                                </small>
                            )}
                            {studentInfo.tennisResumeFileName && !removeTennisResume && (
                                <button
                                    type="button"
                                    className={styles.academicsEditRemoveBtn}
                                    onClick={() => {
                                        setRemoveTennisResume(true);
                                        setTennisResumeFile(null);
                                        setTennisResumeError(null);
                                    }}
                                    data-testid={`${TESTID_PREFIX}.tennis-resume-remove`}
                                >
                                    Remove current tennis resume
                                </button>
                            )}
                        </AcademicsFormField>
                    </div>
                </div>

                {error && (
                    <div className={styles.academicsEditError} data-testid={`${TESTID_PREFIX}.error`}>
                        {error}
                    </div>
                )}

                <div className={styles.academicsEditActions}>
                    <button
                        type="button"
                        className={styles.academicsEditCancelBtn}
                        onClick={onClose}
                        disabled={submitting}
                        data-testid={`${TESTID_PREFIX}.cancel-btn`}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={styles.academicsEditSaveBtn}
                        disabled={submitting}
                        data-testid={`${TESTID_PREFIX}.save-btn`}
                    >
                        {submitting ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </form>
        </PopupV2>
    );
};

AcademicsEditModal.propTypes = {
    onClose: PropTypes.func.isRequired,
};

export default AcademicsEditModal;
